/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import React from "react";
import {
  Input,
  Label,
  Stack,
  Text,
  RSSelectField,
  PrefixSelect,
  MyPhoneInput,
} from "components";
import classNames from "classnames";
import {
  DynamicField,
  DynamicFieldValues,
  DynamicFieldErrors,
} from "./useDynamicFields";
import RichTextEditor from "components/RichTextEditor";
import { PREFIX_OPTIONS, OTHER_PREFIX_VALUE } from "constants/prefixes";
import { getSelectFieldOptions } from "./SelectFieldOptions";
import { ENGLISH_COUNTRIES_CODES } from "constants/countries";
import { getFieldDisplayLabel, isPrefixField } from "./fieldLabelUtils";
import { useTranslation } from "react-i18next";

const SpanErrorMessage: React.FC<{ error: string }> = ({ error }) => {
  if (!error || error.length === 0) return null;

  return (
    <div className="w-full !mt-0">
      <span
        className="text-sm -text--theme-colors-danger-default absolute"
        data-error
      >
        {error}
      </span>
    </div>
  );
};

const FieldSet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack className="space-y-2.5 relative w-full">{children}</Stack>
);

const normalizeFieldOptions = (field: DynamicField) => {
  return (
    field.options?.map((option, index) => {
      if (typeof option === "string") {
        return { id: index.toString(), label: option, value: option };
      }
      return {
        id: (option.id ?? index).toString(),
        label: option.label,
        submissionValue: option.value ?? option.label,
      };
    }) || []
  );
};

interface BaseFieldProps {
  fieldKey: string;
  field: DynamicField;
  value: string | string[];
  error: string;
  onChange: (fieldKey: string, value: string | string[]) => void;
  isReviewing?: boolean;
  isFormDisabled?: boolean;
  defaultCountryCode?: string;
  isCompanion?: boolean;
  nationality?: string;
  countryCode?: string;
  emergencyCountryCode?: string;
  assistantCountryCode?: string;
  setCountryCode?: (code: string) => void;
  setEmergencyCountryCode?: (code: string) => void;
  setAssistantCountryCode?: (code: string) => void;
}

interface PrefixFieldProps extends BaseFieldProps {
  customPrefixValue?: string;
  onCustomPrefixChange?: (value: string) => void;
  customPrefixError?: string;
}

// Renders the prefix field
const PrefixFieldRenderer: React.FC<PrefixFieldProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
  customPrefixValue,
  onCustomPrefixChange,
  customPrefixError,
}) => {
  const { t } = useTranslation();
  const displayLabel = getFieldDisplayLabel(field, fieldKey);

  return (
    <FieldSet>
      {isReviewing ? (
        <>
          <Label isPreview>{displayLabel}</Label>
          <Text className="-text--theme-colors-text-body text-xl">
            {value === OTHER_PREFIX_VALUE
              ? customPrefixValue || "-"
              : value === ""
                ? t("common.labels.preferNotToSay")
                : value || "-"}
          </Text>
        </>
      ) : (
        <>
          <PrefixSelect
            label={displayLabel}
            error={error}
            name={fieldKey}
            id={fieldKey}
            options={PREFIX_OPTIONS}
            value={typeof value === "string" ? value : ""}
            isDisabled={isFormDisabled}
            onChange={(val) => onChange(fieldKey, val)}
          />
          {field.description && (
            <Text className="text-sm -text--component-colors-field-filled-description mt-1 break-words whitespace-pre-wrap">
              {field.description}
            </Text>
          )}
          <SpanErrorMessage error={error} />

          {/* Show custom prefix input when "Other" is selected */}
          {value === OTHER_PREFIX_VALUE && (
            <div>
              <Input
                label={`${displayLabel} - ${t("common.labels.other")}`}
                type="text"
                value={customPrefixValue || ""}
                onChange={(e) => onCustomPrefixChange?.(e.target.value)}
                error={!!customPrefixError}
                required={field.is_required}
                isPreview={isReviewing}
                disabled={isFormDisabled}
                id={`${fieldKey}_custom`}
                name={`${fieldKey}_custom`}
                placeholder={t("guestInfoPage.typePlaceholder")}
                labelClassName="w-full mt-4"
              />
              <SpanErrorMessage error={customPrefixError || ""} />
            </div>
          )}
        </>
      )}
    </FieldSet>
  );
};

// Renders text, number, email, and date input fields
const TextFieldRenderer: React.FC<BaseFieldProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
  isCompanion,
  nationality,
}) => {
  const { t } = useTranslation();
  const handleTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChange(fieldKey, e.target.value);
  };

  // Map field types to valid HTML input types
  const getInputType = (
    fieldType: string,
  ):
    | "number"
    | "text"
    | "date"
    | "email"
    | "textarea"
    | "search"
    | "time"
    | "datetime-local"
    | "password"
    | "tel"
    | "url" => {
    switch (fieldType) {
      case "number":
        return "number";
      case "email":
        return "email";
      case "date":
        return "date";
      case "textarea":
        return "textarea";
      default:
        return "text";
    }
  };

  // Check if document expiration date is less than 6 months from current date
  const shouldShowExpirationWarning = () => {
    if (
      fieldKey !== "document_expiration_date" ||
      !value ||
      typeof value !== "string"
    ) {
      return false;
    }

    const selectedDate = new Date(value);
    const currentDate = new Date();
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(currentDate.getMonth() + 6);

    return selectedDate < sixMonthsFromNow;
  };

  const displayLabel = getFieldDisplayLabel(field, fieldKey);

  return (
    <FieldSet>
      <Input
        label={displayLabel}
        type={getInputType(field.type)}
        value={typeof value === "string" ? value : ""}
        onChange={handleTextChange}
        error={error.length > 0}
        required={field.is_required}
        isPreview={isReviewing}
        disabled={isFormDisabled}
        id={fieldKey}
        name={fieldKey}
        inputClassName={classNames({
          "appearance-none": field.type === "date",
        })}
      />
      {field.description && (
        <Text className="text-sm -text--component-colors-field-filled-description mt-1 break-words whitespace-pre-wrap">
          {field.description}
        </Text>
      )}
      {shouldShowExpirationWarning() && (
        <div className="mt-1">
          <Text className="text-sm -text--theme-colors-warning-default font-medium">
            {t("guestDetails.passportWarning")}
          </Text>
        </div>
      )}
      <SpanErrorMessage error={error} />
    </FieldSet>
  );
};

// Renders select dropdown fields using predefined options
const SelectFieldRenderer: React.FC<BaseFieldProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
}) => {
  // Only use getSelectFieldOptions for string keys
  const selectOptions =
    typeof field.key === "string" ? getSelectFieldOptions(field.key) : [];
  const displayLabel = getFieldDisplayLabel(field, fieldKey);
  const { t } = useTranslation();

  return (
    <FieldSet>
      <RSSelectField
        id={fieldKey}
        name={fieldKey}
        label={displayLabel}
        description={field.description}
        required={field.is_required}
        disabled={isFormDisabled}
        error={error}
        isPreview={isReviewing}
        isClearable={false}
        placeholder={field.placeholder || t("common.labels.select")}
        options={selectOptions}
        value={typeof value === "string" ? value : ""}
        onChange={(val) => onChange(fieldKey, val)}
      />
      <SpanErrorMessage error={error} />
    </FieldSet>
  );
};

// Renders dropdown list fields with normalized options
const DropdownListFieldRenderer: React.FC<BaseFieldProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
}) => {
  const normalizedOptions = normalizeFieldOptions(field);
  const displayLabel = getFieldDisplayLabel(field, fieldKey);
  const { t } = useTranslation();

  return (
    <FieldSet>
      <RSSelectField
        id={fieldKey}
        name={fieldKey}
        label={displayLabel}
        description={field.description}
        required={field.is_required}
        disabled={isFormDisabled}
        error={error}
        isPreview={isReviewing}
        isClearable={false}
        placeholder={t("guestInfoPage.selectPlaceholder")}
        options={normalizedOptions.map((o) => ({
          label: o.label,
          value: o.id,
        }))}
        value={typeof value === "string" ? value : ""}
        onChange={(val) => onChange(fieldKey, val)}
      />
      <SpanErrorMessage error={error} />
    </FieldSet>
  );
};

// Renders single select fields with radio buttons
const SingleSelectFieldRenderer: React.FC<BaseFieldProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
}) => {
  const normalizedOptions = normalizeFieldOptions(field);
  const displayLabel = getFieldDisplayLabel(field, fieldKey);

  return (
    <FieldSet>
      <Label required={field.is_required} isPreview={isReviewing}>
        {displayLabel}
      </Label>
      {field.description && (
        <Text className="text-sm -text--component-colors-field-default-description mt-1 mb-2 break-words whitespace-pre-wrap">
          {field.description}
        </Text>
      )}
      <div className={`space-y-2 ${isReviewing ? "opacity-60" : ""}`}>
        {normalizedOptions.map((option) => {
          const isChecked = value === option.id;
          return (
            <div
              key={`${fieldKey}_${option.id}`}
              className="flex items-center gap-x-2"
            >
              <div className="relative">
                <input
                  id={`${fieldKey}_${option.id}`}
                  name={fieldKey}
                  type="radio"
                  checked={isChecked}
                  onChange={() => {
                    if (!isReviewing) {
                      onChange(fieldKey, option.id);
                    }
                  }}
                  disabled={isFormDisabled || isReviewing}
                  className="sr-only"
                />
                <div
                  className={classNames(
                    "border flex items-center justify-center cursor-pointer h-5 w-5 rounded-full p-1",
                    {
                      "-border--component-colors-check-radio-inactive-icon -text--component-colors-check-radio-inactive-icon hover:-text--component-colors-check-radio-hover-icon hover:-border--component-colors-check-radio-hover-icon":
                        !isChecked && !isFormDisabled && !isReviewing,
                      "-border--component-colors-check-radio-selected-icon -text--component-colors-check-radio-selected-icon hover:-text--component-colors-check-radio-hover-icon hover:-border--component-colors-check-radio-hover-icon":
                        isChecked && !isFormDisabled && !isReviewing,
                      "-bg--component-colors-check-radio-disabled-icon-fill -border--component-colors-check-radio-disabled-icon cursor-not-allowed":
                        isFormDisabled || isReviewing,
                      "opacity-60": isReviewing,
                    },
                  )}
                  onClick={() => {
                    if (!isReviewing && !isFormDisabled) {
                      onChange(fieldKey, option.id);
                    }
                  }}
                >
                  {isChecked && (
                    <div className="w-full h-full -bg--theme-colors-action-primary-default-bg rounded-full"></div>
                  )}
                </div>
              </div>
              <Label
                htmlFor={`${fieldKey}_${option.id}`}
                className={`text-sm font-normal ${
                  isReviewing ? "cursor-default" : "cursor-pointer"
                }`}
              >
                {option.label}
              </Label>
            </div>
          );
        })}
      </div>
      <SpanErrorMessage error={error} />
    </FieldSet>
  );
};

// Renders multi select fields with checkboxes
const MultiSelectFieldRenderer: React.FC<BaseFieldProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
}) => {
  const normalizedOptions = normalizeFieldOptions(field);
  const displayLabel = getFieldDisplayLabel(field, fieldKey);

  return (
    <FieldSet>
      <Label required={field.is_required} isPreview={isReviewing}>
        {displayLabel}
      </Label>
      {field.description && (
        <Text className="text-sm -text--component-colors-field-default-description mt-1 mb-2 break-words whitespace-pre-wrap">
          {field.description}
        </Text>
      )}
      <div className={`space-y-2 ${isReviewing ? "opacity-80" : ""}`}>
        {normalizedOptions.map((option) => {
          const isChecked = Array.isArray(value) && value.includes(option.id);
          return (
            <div
              key={`${fieldKey}_${option.id}`}
              className="flex items-center gap-x-2"
            >
              <div className="relative">
                <input
                  id={`${fieldKey}_${option.id}`}
                  name={`${fieldKey}[]`}
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (!isReviewing) {
                      const checked = e.target.checked;
                      const current = Array.isArray(value) ? value : [];
                      const newVals = checked
                        ? [...current, option.id]
                        : current.filter((v) => v !== option.id);
                      onChange(fieldKey, newVals);
                    }
                  }}
                  disabled={isFormDisabled || isReviewing}
                  className="sr-only"
                />
                <div
                  className={classNames(
                    "border flex items-center justify-center cursor-pointer h-5 w-5 -rounded-checkbox",
                    {
                      "-border--component-colors-check-radio-inactive-icon -text--component-colors-check-radio-inactive-icon hover:-text--component-colors-check-radio-hover-icon hover:-border--component-colors-check-radio-hover-icon":
                        !isChecked && !isFormDisabled && !isReviewing,
                      "-border--component-colors-check-radio-selected-icon -text--component-colors-check-radio-selected-icon hover:-text--component-colors-check-radio-hover-icon hover:-border--component-colors-check-radio-hover-icon":
                        isChecked && !isFormDisabled && !isReviewing,
                      "-bg--component-colors-check-radio-disabled-icon-fill -border--component-colors-check-radio-disabled-icon cursor-not-allowed":
                        isFormDisabled || isReviewing,
                      "opacity-60": isReviewing,
                    },
                  )}
                  onClick={() => {
                    if (!isReviewing && !isFormDisabled) {
                      const newChecked = !isChecked;
                      const current = Array.isArray(value) ? value : [];
                      const newVals = newChecked
                        ? [...current, option.id]
                        : current.filter((v) => v !== option.id);
                      onChange(fieldKey, newVals);
                    }
                  }}
                >
                  {isChecked && (
                    <svg
                      className="absolute -fill--theme-colors-action-primary-default-bg"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      height="12"
                      width="12"
                    >
                      <path
                        clipRule="evenodd"
                        fillRule="evenodd"
                        d="M21.652,3.211c-0.293-0.295-0.77-0.295-1.061,0L9.41,14.34c-0.293,0.297-0.771,0.297-1.062,0L3.449,9.351C3.304,9.203,3.114,9.13,2.923,9.129C2.73,9.128,2.534,9.201,2.387,9.351l-2.165,1.946C0.078,11.445,0,11.63,0,11.823c0,0.194,0.078,0.397,0.223,0.544l4.94,5.184c0.292,0.296,0.771,0.776,1.062,1.07l2.124,2.141c0.292,0.293,0.769,0.293,1.062,0l14.366-14.34c0.293-0.294,0.293-0.777,0-1.071L21.652,3.211z"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <Label
                htmlFor={`${fieldKey}_${option.id}`}
                className={`text-sm font-normal ${
                  isReviewing ? "cursor-default" : "cursor-pointer"
                }`}
              >
                {option.label}
              </Label>
            </div>
          );
        })}
      </div>
      <SpanErrorMessage error={error} />
    </FieldSet>
  );
};

// Renders checkbox fields
const CheckboxFieldRenderer: React.FC<BaseFieldProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
}) => {
  const isChecked =
    value === "true" || value === "1" || (typeof value === "boolean" && value);
  const isTermsAndConditions = field.type === "terms_and_conditions";
  const displayLabel = getFieldDisplayLabel(field, fieldKey);

  return (
    <FieldSet>
      <div
        className={`flex items-center gap-x-2 ${
          isReviewing ? "opacity-80" : ""
        }`}
      >
        <div className="relative">
          <input
            id={fieldKey}
            name={fieldKey}
            type="checkbox"
            checked={isChecked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (!isReviewing) {
                onChange(fieldKey, e.target.checked ? "true" : "false");
              }
            }}
            disabled={isFormDisabled || isReviewing}
            className="sr-only"
          />
          <div
            className={classNames(
              "border flex items-center justify-center cursor-pointer h-5 w-5 -rounded-checkbox",
              {
                "-border--component-colors-check-radio-inactive-icon -text--component-colors-check-radio-inactive-icon hover:-text--component-colors-check-radio-hover-icon hover:-border--component-colors-check-radio-hover-icon":
                  !isChecked && !isFormDisabled && !isReviewing,
                "-border--component-colors-check-radio-selected-icon -text--component-colors-check-radio-selected-icon hover:-text--component-colors-check-radio-hover-icon hover:-border--component-colors-check-radio-hover-icon":
                  isChecked && !isFormDisabled && !isReviewing,
                "-bg--component-colors-check-radio-disabled-icon-fill -border--component-colors-check-radio-disabled-icon cursor-not-allowed":
                  isFormDisabled || isReviewing,
                "opacity-60": isReviewing,
              },
            )}
            onClick={() => {
              if (!isReviewing && !isFormDisabled) {
                onChange(fieldKey, !isChecked ? "true" : "false");
              }
            }}
          >
            {isChecked && (
              <svg
                className="absolute -fill--theme-colors-action-primary-default-bg"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                height="12"
                width="12"
              >
                <path
                  clipRule="evenodd"
                  fillRule="evenodd"
                  d="M21.652,3.211c-0.293-0.295-0.77-0.295-1.061,0L9.41,14.34c-0.293,0.297-0.771,0.297-1.062,0L3.449,9.351C3.304,9.203,3.114,9.13,2.923,9.129C2.73,9.128,2.534,9.201,2.387,9.351l-2.165,1.946C0.078,11.445,0,11.63,0,11.823c0,0.194,0.078,0.397,0.223,0.544l4.94,5.184c0.292,0.296,0.771,0.776,1.062,1.07l2.124,2.141c0.292,0.293,0.769,0.293,1.062,0l14.366-14.34c0.293-0.294,0.293-0.777,0-1.071L21.652,3.211z"
                />
              </svg>
            )}
          </div>
        </div>
        <Label
          htmlFor={fieldKey}
          className={`text-sm font-normal ${
            isReviewing ? "cursor-default" : "cursor-pointer"
          }`}
          required={field.is_required}
        >
          {isTermsAndConditions ? (
            <span
              className="inline-block"
              dangerouslySetInnerHTML={{
                __html: (field.options?.[0] as string) || "",
              }}
            />
          ) : (
            displayLabel
          )}
        </Label>
      </div>
      {field.description && (
        <Text className="text-sm -text--component-colors-field-default-description mt-1 break-words whitespace-pre-wrap">
          {field.description}
        </Text>
      )}
      <SpanErrorMessage error={error} />
    </FieldSet>
  );
};

// Renders phone input fields
const PhoneFieldRenderer: React.FC<BaseFieldProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
  defaultCountryCode,
  countryCode,
  emergencyCountryCode,
  assistantCountryCode,
  setCountryCode,
  setEmergencyCountryCode,
  setAssistantCountryCode,
}) => {
  // Determine which country code and setter to use based on the field key structure
  const getCountryCodeProps = () => {
    if (typeof field.key === "object" && field.key.code && field.key.number) {
      const codeKey = field.key.code;

      if (codeKey === "assistant_country_code") {
        return {
          currentCountryCode: assistantCountryCode || defaultCountryCode || "",
          setCountryCodeFn: setAssistantCountryCode || (() => {}),
        };
      } else if (
        codeKey === "emergency_contact_country_code" ||
        codeKey === "emergency_country_code"
      ) {
        return {
          currentCountryCode: emergencyCountryCode || defaultCountryCode || "",
          setCountryCodeFn: setEmergencyCountryCode || (() => {}),
        };
      } else if (codeKey === "country_code") {
        return {
          currentCountryCode: countryCode || defaultCountryCode || "",
          setCountryCodeFn: setCountryCode || (() => {}),
        };
      }
    }

    // Fallback to default
    return {
      currentCountryCode: defaultCountryCode || "",
      setCountryCodeFn: () => {},
    };
  };

  const { currentCountryCode, setCountryCodeFn } = getCountryCodeProps();
  const displayLabel = getFieldDisplayLabel(field, fieldKey);

  return (
    <FieldSet>
      {isReviewing ? (
        <>
          <Label isPreview>{displayLabel}</Label>
          <Text className="-text--theme-colors-text-body text-xl">
            {currentCountryCode && typeof value === "string" && value
              ? `+${currentCountryCode} ${value}`
              : (typeof value === "string" ? value : "") || "-"}
          </Text>
        </>
      ) : (
        <>
          <Label required={field.is_required}>{displayLabel}</Label>
          <MyPhoneInput
            value={typeof value === "string" ? value : ""}
            countryCode={currentCountryCode}
            isMissing={error.length > 0}
            isCodeMissing={false}
            onChange={(phoneNumber) => onChange(fieldKey, phoneNumber)}
            setCountryCode={setCountryCodeFn}
            disabled={isFormDisabled}
          />
        </>
      )}
      {field.description && (
        <Text className="text-sm -text--component-colors-field-filled-description mt-1">
          {field.description}
        </Text>
      )}
      <SpanErrorMessage error={error} />
    </FieldSet>
  );
};

// Renders rich text fields (text or document)
const RichTextFieldRenderer: React.FC<BaseFieldProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
}) => {
  const displayLabel = getFieldDisplayLabel(field, fieldKey);

  return (
    <FieldSet>
      <Stack className="gap-y-2">
        {!isReviewing && (
          <>
            <Label>{displayLabel}</Label>
            <RichTextEditor
              {...({ name: fieldKey, id: fieldKey } as any)}
              value={typeof value === "string" ? value : ""}
              onChange={(val: string) => onChange(fieldKey, val)}
              maxLength={2000}
            />
          </>
        )}
        {isReviewing && value && (
          <>
            <Label isPreview>{displayLabel}</Label>
            <div
              className="-text--theme-colors-text-body text-xl"
              dangerouslySetInnerHTML={{
                __html: typeof value === "string" ? value : "",
              }}
            />
          </>
        )}
      </Stack>
      {field.description && (
        <Text className="text-sm -text--component-colors-field-default-description mt-1">
          {field.description}
        </Text>
      )}
      <SpanErrorMessage error={error} />
    </FieldSet>
  );
};

interface DynamicFieldRendererProps {
  sectionKey: string;
  fieldKey: string;
  field: DynamicField;
  value: string | string[];
  error: string;
  onChange: (fieldKey: string, value: string | string[]) => void;
  isReviewing?: boolean;
  isFormDisabled?: boolean;
  customPrefixValue?: string;
  onCustomPrefixChange?: (value: string) => void;
  customPrefixError?: string;
  defaultCountryCode?: string;
  isCompanion?: boolean;
  nationality?: string;
  countryCode?: string;
  emergencyCountryCode?: string;
  assistantCountryCode?: string;
  setCountryCode?: (code: string) => void;
  setEmergencyCountryCode?: (code: string) => void;
  setAssistantCountryCode?: (code: string) => void;
}

const DynamicFieldRenderer: React.FC<DynamicFieldRendererProps> = ({
  fieldKey,
  field,
  value,
  error,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
  customPrefixValue,
  onCustomPrefixChange,
  customPrefixError,
  defaultCountryCode,
  isCompanion,
  nationality,
  countryCode,
  emergencyCountryCode,
  assistantCountryCode,
  setCountryCode,
  setEmergencyCountryCode,
  setAssistantCountryCode,
}) => {
  // Early return if field is not visible
  if (!field.is_visible) return null;

  // Determine field type and render appropriate component
  const baseProps: BaseFieldProps = {
    fieldKey,
    field,
    value,
    error,
    onChange,
    isReviewing,
    isFormDisabled,
    defaultCountryCode,
    isCompanion,
    nationality,
    countryCode,
    emergencyCountryCode,
    assistantCountryCode,
    setCountryCode,
    setEmergencyCountryCode,
    setAssistantCountryCode,
  };

  const prefixProps: PrefixFieldProps = {
    ...baseProps,
    customPrefixValue,
    onCustomPrefixChange,
    customPrefixError,
  };

  if (field.type === "prefix" || isPrefixField(field, fieldKey)) {
    return <PrefixFieldRenderer {...prefixProps} />;
  }

  switch (field.type) {
    case "text":
    case "number":
    case "email":
    case "link":
    case "single_line_text":
    case "long_text":
    case "textarea":
    case "date":
      return <TextFieldRenderer {...baseProps} />;

    case "select":
      return <SelectFieldRenderer {...baseProps} />;

    case "dropdown_list":
      return <DropdownListFieldRenderer {...baseProps} />;

    case "single_select":
      return <SingleSelectFieldRenderer {...baseProps} />;

    case "multi_select":
      return <MultiSelectFieldRenderer {...baseProps} />;

    case "checkbox":
    case "terms_and_conditions":
      return <CheckboxFieldRenderer {...baseProps} />;

    case "phone_number":
      return <PhoneFieldRenderer {...baseProps} />;

    case "text_or_document":
      return <RichTextFieldRenderer {...baseProps} />;

    default:
      return null;
  }
};

interface DynamicFieldsProps {
  sectionKey: string;
  fields: Record<string, DynamicField>;
  values: DynamicFieldValues;
  errors: DynamicFieldErrors;
  onChange: (fieldKey: string, value: string | string[]) => void;
  isReviewing?: boolean;
  isFormDisabled?: boolean;
  customPrefixValue?: string;
  onCustomPrefixChange?: (value: string) => void;
  customPrefixError?: string;
  defaultCountryCode?: string;
  isCompanion?: boolean;
  nationality?: string;
  countryCode?: string;
  emergencyCountryCode?: string;
  assistantCountryCode?: string;
  setCountryCode?: (code: string) => void;
  setEmergencyCountryCode?: (code: string) => void;
  setAssistantCountryCode?: (code: string) => void;
}

const DynamicFields: React.FC<DynamicFieldsProps> = ({
  sectionKey,
  fields,
  values,
  errors,
  onChange,
  isReviewing = false,
  isFormDisabled = false,
  customPrefixValue,
  onCustomPrefixChange,
  customPrefixError,
  defaultCountryCode,
  isCompanion,
  nationality,
  countryCode,
  emergencyCountryCode,
  assistantCountryCode,
  setCountryCode,
  setEmergencyCountryCode,
  setAssistantCountryCode,
}) => {
  const shouldSpanFullWidth = (
    fieldKey: string,
    field: DynamicField,
  ): boolean => {
    // Field types that should always be full width
    const fullWidthFieldTypes = [
      "single_select",
      "multi_select",
      "checkbox",
      "text_or_document",
      "terms_and_conditions",
    ];

    // Specific field keys that should be full width
    const fullWidthFieldKeys = ["first_name", "native_first_name"];

    // Check if it's a prefix field
    const isFieldPrefix = isPrefixField(field, fieldKey);

    return (
      fullWidthFieldTypes.includes(field.type) ||
      fullWidthFieldKeys.includes(fieldKey) ||
      isFieldPrefix
    );
  };

  // Handle all fields, both custom and non-custom, sort by optional `order`
  const allFields = Object.entries(fields)
    .map(([fieldKey, field], index) => ({ fieldKey, field, index }))
    .sort((a, b) => {
      const ao = Number(a.field.order);
      const bo = Number(b.field.order);
      const aHas = Number.isFinite(ao);
      const bHas = Number.isFinite(bo);
      if (aHas && bHas) return ao - bo;
      if (aHas) return -1; // ordered fields first
      if (bHas) return 1;
      return a.index - b.index; // preserve original order when both unordered
    });

  // Filter out hidden fields and calculate layout
  const visibleFields = allFields.filter(({ fieldKey, field }) => {
    const shouldHideField = () => {
      // Hide relationship field if user is not a companion
      if (fieldKey === "relationship" && !isCompanion) {
        return true;
      }

      // Hide native name fields if nationality is one of the English-speaking countries
      if (
        fieldKey === "native_first_name" &&
        nationality &&
        ENGLISH_COUNTRIES_CODES.includes(nationality)
      ) {
        return true;
      }

      return false;
    };

    return !shouldHideField();
  });

  // Determine which fields should be full width
  const shouldFieldBeFullWidth = (currentIndex: number): boolean => {
    const { fieldKey, field } = visibleFields[currentIndex];
    const isNaturallyFullWidth = shouldSpanFullWidth(fieldKey, field);

    if (isNaturallyFullWidth) return true;

    // Find consecutive half-width fields
    const halfWidthGroups: number[][] = [];
    let currentGroup: number[] = [];

    visibleFields.forEach((item, index) => {
      if (!shouldSpanFullWidth(item.fieldKey, item.field)) {
        currentGroup.push(index);
      } else {
        if (currentGroup.length > 0) {
          halfWidthGroups.push([...currentGroup]);
          currentGroup = [];
        }
      }
    });

    // Don't forget the last group if it exists
    if (currentGroup.length > 0) {
      halfWidthGroups.push(currentGroup);
    }

    // Check if current field is in a group with odd number of half-width fields
    for (const group of halfWidthGroups) {
      if (group.includes(currentIndex)) {
        // If this group has odd number of fields, make the last one full width
        if (
          group.length % 2 === 1 &&
          currentIndex === group[group.length - 1]
        ) {
          return true;
        }
        break;
      }
    }

    return false;
  };

  if (visibleFields.length === 0) return null;

  return (
    <div className="grid md:grid-cols-2 md:gap-x-8 gap-y-8">
      {visibleFields.map(({ fieldKey, field }, index) => {
        const isFullWidth = shouldFieldBeFullWidth(index);

        return (
          <div key={fieldKey} className={isFullWidth ? "md:col-span-2" : ""}>
            <DynamicFieldRenderer
              sectionKey={sectionKey}
              fieldKey={fieldKey}
              field={field}
              value={
                values[fieldKey] || (field.type === "multi_select" ? [] : "")
              }
              error={errors[fieldKey] || ""}
              onChange={onChange}
              isReviewing={isReviewing}
              isFormDisabled={isFormDisabled}
              customPrefixValue={customPrefixValue}
              onCustomPrefixChange={onCustomPrefixChange}
              customPrefixError={customPrefixError}
              defaultCountryCode={defaultCountryCode}
              isCompanion={isCompanion}
              nationality={nationality}
              countryCode={countryCode}
              emergencyCountryCode={emergencyCountryCode}
              assistantCountryCode={assistantCountryCode}
              setCountryCode={setCountryCode}
              setEmergencyCountryCode={setEmergencyCountryCode}
              setAssistantCountryCode={setAssistantCountryCode}
            />
          </div>
        );
      })}
    </div>
  );
};

export default DynamicFields;
